import type { AudioGateMode } from "@rooms/roomsClient";

import type { CaptureNode, RoomAudioGateCommand } from "./captureClient";

interface RoomAudioGateDesired {
  mode: AudioGateMode;
  /** dB above the learned floor; null = Zuul's env default (12). */
  marginDb: number | null;
  revision: number;
}

/** A current Zuul always reports a mode, so this node can never catch up. */
const predatesGate = (node: CaptureNode): boolean =>
  node.connectorLive && node.gateMode === null && node.gateRevision === 0;

/**
 * Like `roomCaptureState` but with no device ack: a node is caught up once its
 * reported `gateRevision` reaches the desired one. `failed`: Zuul refused the
 * mode (e.g. `fixed` with no RMS configured) and runs another.
 */
export const roomAudioGateState = (
  nodes: CaptureNode[] | undefined,
  roomDesired: Partial<RoomAudioGateDesired>,
  roomUuid: string,
  command?: RoomAudioGateCommand,
) => {
  const rosterLoaded = nodes !== undefined;
  const knownNodes = nodes ?? [];
  const nodeDesired = knownNodes.reduce<RoomAudioGateDesired | null>(
    (latest, node) => {
      if (node.desiredGateRevision === 0) return latest;
      if (latest && latest.revision > node.desiredGateRevision) return latest;
      return {
        mode: node.desiredGateMode,
        marginDb: node.desiredGateMarginDb,
        revision: node.desiredGateRevision,
      };
    },
    null,
  );
  const serverDesired: RoomAudioGateDesired = nodeDesired ?? {
    mode: roomDesired.mode ?? null,
    marginDb: roomDesired.marginDb ?? null,
    revision: roomDesired.revision ?? 0,
  };
  const desired: RoomAudioGateDesired =
    command?.roomUuid === roomUuid && command.revision > serverDesired.revision
      ? {
          mode: command.mode,
          marginDb: command.marginDb,
          revision: command.revision,
        }
      : serverDesired;

  const reporting = knownNodes.filter((node) => !predatesGate(node));
  const unreported =
    rosterLoaded && knownNodes.length > 0 && reporting.length === 0;
  const pending =
    rosterLoaded &&
    desired.revision > 0 &&
    !unreported &&
    (knownNodes.length === 0 ||
      reporting.some((node) => node.gateRevision < desired.revision));
  const failed =
    desired.mode !== null &&
    reporting.some(
      (node) =>
        node.gateRevision >= desired.revision &&
        node.gateMode !== null &&
        node.gateMode !== desired.mode,
    );
  const unavailable =
    rosterLoaded &&
    (knownNodes.length === 0 ||
      knownNodes.every((node) => !node.connectorLive));

  // The streaming node's reading wins; otherwise any node that knows its floor.
  const source =
    knownNodes.find((node) => node.gateState !== null) ??
    knownNodes.find((node) => node.gateFloor !== null) ??
    knownNodes[0];

  return {
    mode: desired.mode,
    marginDb: desired.marginDb,
    revision: desired.revision,
    pending,
    failed,
    unavailable,
    unreported,
    reading: {
      mode: source?.gateMode ?? null,
      state: source?.gateState ?? null,
      level: source?.gateLevel ?? null,
      floor: source?.gateFloor ?? null,
      threshold: source?.gateThreshold ?? null,
      marginDb: source?.gateMarginDb ?? null,
      open: source?.gateOpen ?? null,
    },
  };
};

export type RoomAudioGateState = ReturnType<typeof roomAudioGateState>;
type RoomAudioGateReading = RoomAudioGateState["reading"];

const levelWords = (level: number, floor: number | null): string => {
  if (floor === null || floor <= 0) return "no floor yet";
  const db = Math.round(20 * Math.log10(level / floor));
  if (!Number.isFinite(db)) return "silent";
  if (db > 0) return `${db} dB above floor`;
  if (db < 0) return `${-db} dB below floor`;
  return "at floor";
};

/** Staff set margins in dB but Zuul measures raw RMS, so RMS figures carry dB where known. */
export const describeGateReading = (reading: RoomAudioGateReading): string => {
  const level =
    reading.level === null
      ? "level idle"
      : `level ${Math.round(reading.level)} (${levelWords(reading.level, reading.floor)})`;
  const floor =
    reading.floor === null
      ? "floor unlearned"
      : `floor ${Math.round(reading.floor)}`;
  // Threshold 0 is bypass, not a number worth quoting.
  let threshold = "threshold none";
  if (reading.threshold !== null && reading.threshold > 0) {
    const margin =
      reading.marginDb !== null
        ? `${reading.marginDb} dB`
        : reading.state === "fixed"
          ? "fixed RMS"
          : "no margin";
    threshold = `threshold ${Math.round(reading.threshold)} (${margin})`;
  }
  return `${level} / ${floor} / ${threshold}`;
};
